<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import { useElementalListContext } from '@/components/DrawerSidebarRight/components/shared/ElementalList/ElementalListContext'
import ElementalItemButton from '@/components/DrawerSidebarRight/components/shared/ElementalItemButton/index.vue'
import FloatMenu from '@/components/FloatMenu/indev.vue'
import MenuItem from '@/components/FloatMenu/FloatMenuOption/indev.vue'
import { type Elemental, ElementalTypeEnum, type ElementalTypeRoutes } from '~~/src/types/elemental'
import { useDeleteElemental } from '@//composables/mutations/elemetal/useDeleteElemental'
import SaveElementalMenuItem from '@/components/DrawerSidebarRight/components/shared/SaveElemental/index.vue'
import RemoveSaveElementalItem from '@/components/DrawerSidebarRight/components/shared/RemoveSaveElemental/index.vue'
import DownloadKnowledgeBase from '@/components/ElemetalMenu/DownloadKnowledgeBase/index.vue'
import CopyIdOption from '@/components/ElemetalMenu/CopyIdOption/index.vue'
import CopyElementalLinkOption from '@/components/ElemetalMenu/CopyElementalLinkOption/index.vue'
import OpenElementalLinkOption from '@/components/ElemetalMenu/OpenElementalLinkOption/index.vue'
import OpenViewFeedOption from '@/components/ElemetalMenu/OpenViewFeedOption/index.vue'
import DuplicateElementalOption from '@/components/ElemetalMenu/DuplicateElementalOption/index.vue'
import { getElementalType } from '@/utils/elementals'
import { useUser } from '@/composables/auth.client'
import { useSidebarStore } from '~~/src/stores/sidebar'

const props = withDefaults(defineProps<{
  elemental: Elemental
  isListElement?: boolean
  showEditAction?: boolean
  showOpenAction?: boolean
  hiddeDuplicateAction?: boolean
  hiddeDeleteAction?: boolean
  hiddeCopyLinkAction?: boolean
  hiddeSaveAction?: boolean
  hiddeOpenExternalAction?: boolean
}>(), {
  showEditAction: true,
  showOpenAction: true,
})
const { mutateAsync } = useDeleteElemental()

const user = useUser()

const { listId, parentListId, onList, isFolder } = useElementalListContext()

const currentListId = computed(() => props.isListElement ? parentListId : listId)

const isOwner = computed(() => {
  return user.value?.id === props.elemental.user.id
})

function wordsFistLetterToUpperCase(str: string) {
  return str.replace(/\b\w/g, l => l.toUpperCase())
}

const router = useRouter()
const isList = !isNaN(Number(props.elemental.id))
const elementalLabel = props.isListElement ? 'list' : getElementalTypeRoute(getElementalType(props.elemental.elementalTypeId, isList))

const { toggleSidebar } = useSidebarStore()
const closeSidebar = useDebounceFn(() => toggleSidebar(), 300)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')
function goToEdit() {
  const elementalTypeId = isList ? ElementalTypeEnum.List : (props.elemental.elementalTypeId || props.elemental.elemental_type_id)
  const elementalRouteId = elementalTypeId !== undefined ? elementalTypeId as keyof typeof ElementalTypeRoutes : null
  if (elementalRouteId === null)
    return
  router.push(`/${elementalLabel}/${props.elemental.id}/edit`)
  if (isMobile.value)
    closeSidebar()
}

const handleDelete = async () => {
  const elementalTypeId = isList ? ElementalTypeEnum.List : props.elemental.elementalTypeId
  const elementalRouteId = elementalTypeId !== undefined ? elementalTypeId as keyof typeof ElementalTypeRoutes : null
  if (elementalRouteId === null)
    return

  try {
    await mutateAsync({
      elementalType: elementalRouteId,
      id: props.elemental.id,
      listId: currentListId.value,
      listType: props.elemental.elementalTypeId,
    }, {
      onSuccess: (result) => {
        if (result)
          router.push('/')
        else
          useToast().error(`Failed to delete ${elementalLabel}`)
      },
    })
  }
  catch (error) {

  }
}

defineOptions({
  name: 'ElemetalMenu',
})
</script>

<template>
  <FloatMenu position="bottom" container-class="min-w-38" :offset="4" :class="$props.class">
    <template #default="{ isOpen, toggle }">
      <slot :is-open="isOpen" :toggle="toggle">
        <ElementalItemButton :aria-pressed="isOpen" title="More options" @click="toggle">
          <slot name="icon">
            <div i-material-symbols:more-horiz />
          </slot>
        </ElementalItemButton>
      </slot>
    </template>
    <template #options>
      <MenuItem v-if="isOwner && showEditAction" @select="goToEdit">
        <template #icon>
          <div i-material-symbols:edit-outline w-3 h-3 />
        </template>
        {{ wordsFistLetterToUpperCase(`edit ${elementalLabel}`) }}
      </MenuItem>
      <DownloadKnowledgeBase :elemental="elemental" :is-table="elemental.elementalTypeId === ElementalTypeEnum.Table" />
      <OpenElementalLinkOption
        v-if="!hiddeOpenExternalAction" :elemental="props.elemental"
        :route="elementalLabel"
      />
      <OpenViewFeedOption
        :elemental="props.elemental"
        :route="elementalLabel"
      />

      <DuplicateElementalOption
        v-if="user?.id && !hiddeDuplicateAction" :elemental="props.elemental"
        :route="elementalLabel" :list-id="currentListId"
      />

      <CopyElementalLinkOption v-if="!hiddeCopyLinkAction" :elemental="props.elemental" :route="elementalLabel" />

      <CopyIdOption :elemental-id="props.elemental.id" />

      <SaveElementalMenuItem
        v-if="!isFolder && !elemental.isSaved && onList && !hiddeSaveAction"
        :id="props.elemental.id"
        :elemental-type="getElementalType(props.elemental.elementalTypeId, props.elemental.isList)"
        :list-id="currentListId"
      />
      <RemoveSaveElementalItem
        v-else-if="onList && !hiddeSaveAction" :id="props.elemental.id"
        :elemental-type="getElementalType(props.elemental.elementalTypeId, props.elemental.isList)"
        :list-id="currentListId"
        :list-type="elemental.elementalTypeId"
      />
      <MenuItem v-if="isOwner && !hiddeDeleteAction" @click="handleDelete">
        <template #icon>
          <div i-material-symbols:close w-3 h-3 />
        </template>
        Delete
      </MenuItem>
    </template>
  </FloatMenu>
</template>
